/**
 * Passenger Store
 * ----------------------------
 *
 * This store manages the passengers, separated from bookable products (activities, trips, accommodation etc.)
 * Each passenger equals a person and can be part of one or more products.
 */
export const usePassengerStore = defineStore('passenger', () => {
    // Stores all passengers
    const passengers = ref<Passenger[]>([]);

    /**
     * Adds a new passenger to the store.
     * - Make sure "ref" is recreated, to trigger reactivity (e.g. reload offers from API, remove depending products etc.)
     */
    function addPassenger(passenger: Passenger) {
        const { product } = useProduct();
        setHiddenReductionCards(passenger);
        const i = passengers.value.length;
        passenger.ref = getPassengerRef(i, passenger);
        passengers.value.push(passenger);
        product.value.passengerRefs.push(passenger.ref);
    }

    /**
     * Updates passenger data.
     * Recreates references by default to reload offers and reset dependent selections.
     * Disable for booking-only details that do not affect offers (e.g. rental measurements).
     */
    function updatePassenger(passenger: Passenger, updateReferences = true) {
        const { product } = useProduct();
        setHiddenReductionCards(passenger);
        const previousRef = passenger.ref;
        const i = passengers.value.findIndex(p => p.ref === passenger.ref);
        if (updateReferences) {
            passenger.ref = getPassengerRef(i, passenger);
            product.value.passengerRefs = product.value.passengerRefs.map(ref => ref === previousRef ? passenger.ref : ref);
        }
        passengers.value[i] = passenger;
    }

    /**
     * Removes passengers from the store
     */
    function removePassenger(passenger: Passenger) {
        const { product } = useProduct();
        passengers.value = [
            ...passengers.value.filter(p => p.ref !== passenger.ref),
        ];
        product.value.passengerRefs = product.value.passengerRefs.filter(ref => ref !== passenger.ref);
    }

    /**
     * Remove passengers that are not parts of any product anymore
     */
    function removeObsoletePassengers() {
        const refs = [
            useCartStore().products.flatMap(p => p.passengerRefs),
            useProductStore().products.flatMap(p => p.passengerRefs),
        ];

        passengers.value = [
            ...passengers.value.filter(pax => refs.some(r => r.includes(pax.ref))),
        ];
    }

    /**
     * Add passengers
     */
    function setPassengers(newPassengers: Passenger[]) {
        passengers.value = [
            ...passengers.value.filter(p => !newPassengers.some(ps => ps.ref === p.ref)),
            ...deepClone(newPassengers),
        ];
    }

    /**
     * For some products, we need to add predefined reduction cards to the passenger.
     * For example when buying a JTP, we need to add the Jungfrau Travel Pass reduction card,
     * so the reductioncard is applied for subsequent products (e.g. Anschlussticket JJ).
     *
     * A product flow can have a predefined, hidden "reduction card" (representing the product itself)
     * defined in the config
     */
    function setHiddenReductionCards(passenger: Passenger) {
        const { product } = useProduct();
        if (
            product.value.config.hiddenProductReductionCard
            && !passenger.reductionCards.some(r => r.code === product.value.config.hiddenProductReductionCard)
        ) {
            passenger.reductionCards.push({
                type: 'REDUCTION_CARD',
                isHidden: true,
                code: product.value.config.hiddenProductReductionCard,
                name: product.value.config.hiddenProductReductionCard,
            });
        }
    }

    function reset() {
        passengers.value = [];
    }

    return {
        passengers,
        addPassenger,
        updatePassenger,
        removePassenger,
        removeObsoletePassengers,
        setPassengers,
        reset,
    };
}, {
    persist: {
        ...storePersist.storePersistOptions,
        key: 'passenger-v6',
    },
});
